import { useCallback, useMemo, useRef, useState } from "react";
import { Control, Controller, type FieldValues } from "react-hook-form";

import ClickAwayListener from "@mui/material/ClickAwayListener";
import FormControl from "@mui/material/FormControl";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import TextField, { type TextFieldProps } from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

import { InputLabelHeight } from "@covertree/types";
import { getInputLabelHeight } from "@covertree/utils";
import InfoButton from "@covertree/ui/buttons/InfoButton";
import PasswordIndicator from "./PasswordIndicator";

export type FormPasswordFieldProps<TValues extends FieldValues> = Omit<
  TextFieldProps,
  "variant"
> & {
  id: string;
  name: string; // @TODO investigate why "Path" from "react-hook-form" type is not working
  control: Control<TValues>;
  label: React.ReactNode;
  dataTestId?: string;
  type?: React.InputHTMLAttributes<unknown>["type"];
  error?: React.ReactNode;
  tooltip?: string;
  optional?: boolean;
  disabled?: boolean;
};

export const FormPasswordField = <TValues extends FieldValues>({
  id,
  name,
  control,
  label,
  dataTestId,
  tooltip,
  type = "text",
  optional,
  disabled = false,
  ...textFieldProps
}: FormPasswordFieldProps<TValues>) => {
  const theme = useTheme();
  const labelComponentHeight = useMemo(
    () => getInputLabelHeight(theme, InputLabelHeight.DEFAULT),
    [theme]
  );

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const labelRef = useRef<HTMLParagraphElement>(null);

  const handleTooltipClose = useCallback(() => setTooltipOpen(false), []);

  const handleHelpIconClick = useCallback(() => {
    setTooltipOpen((val) => !val);
  }, []);

  return (
    <Controller
      /* eslint-disable  @typescript-eslint/no-explicit-any */
      name={name as any}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormControl variant="standard" fullWidth error={!!error}>
          <InputLabel
            htmlFor={id}
            id={`${id}-label`}
            sx={{
              position: "static",
              alignItems: "flex-end",
              display: "flex",
              height: labelComponentHeight,
              transform: "none",
              "& > p": {
                display: "flex",
                alignItems: "center",
                flex: 1,
                width: "100%",
                whiteSpace: "break-spaces",
              },
            }}>
            <ClickAwayListener onClickAway={handleTooltipClose}>
              <Tooltip
                onClose={handleTooltipClose}
                open={tooltipOpen}
                disableFocusListener
                disableHoverListener
                disableTouchListener
                PopperProps={{
                  style: { width: labelRef.current?.clientWidth },
                }}
                sx={{
                  margin: theme.spacing(0.75, 0),
                }}
                title={tooltip ?? ""}>
                <Typography variant="body2" ref={labelRef}>
                  {label}
                  {tooltip && (
                    <InfoButton
                      data-testid={`${dataTestId}-tooltip`}
                      onClick={handleHelpIconClick}
                      sx={{
                        marginLeft: theme.spacing(),
                      }}
                    />
                  )}
                </Typography>
              </Tooltip>
            </ClickAwayListener>
          </InputLabel>
          <TextField
            id={id}
            type={type}
            error={!!error}
            disabled={disabled}
            variant="outlined"
            InputProps={{
              endAdornment: optional ? (
                <InputAdornment position="end">
                  <Typography variant="subtitle1">Optional</Typography>
                </InputAdornment>
              ) : undefined,
              inputProps: {
                "data-testid": dataTestId,
                inputMode: textFieldProps.inputMode,
              },
            }}
            {...textFieldProps}
            {...field}
          />
          <PasswordIndicator password={field.value as string} />
        </FormControl>
      )}
    />
  );
};

export default FormPasswordField;
